import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  Chip,
  IconButton,
  Collapse,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import moment from "moment";
import LoadingButton from "@mui/lab/LoadingButton";
import { useField, useForm } from "react-final-form";
import { useApi } from "../../utils/api";
import { useUserInfo } from "../../utils/user";

const ExerciseCommentsSection = ({
  workoutId,
  exerciseIndex,
  defaultExpanded = false,
}) => {
  const { addComment, updateComment, deleteComment } = useApi();
  const { id: currentUserId, name: currentName } = useUserInfo();
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(defaultExpanded);

  useEffect(() => {
    if (defaultExpanded) setExpanded(true);
  }, [defaultExpanded]);

  const form = useForm();
  const path = `exercises[${exerciseIndex}].comments`;
  const {
    input: { value: commentsRaw },
  } = useField(path, {
    subscription: { value: true },
    defaultValue: [],
  });
  const comments = Array.isArray(commentsRaw) ? commentsRaw : [];

  /**
   * Comments are persisted immediately via the comments API, not via Save.
   * Merge into form values with initialize() so initialValues stay in sync and the
   * field stays pristine (does not mark the workout form dirty).
   */
  const applyCommentsPristine = (nextComments) => {
    const values = form.getState().values;
    if (!values?.exercises?.[exerciseIndex]) return;
    form.initialize({
      ...values,
      exercises: values.exercises.map((ex, i) =>
        i === exerciseIndex ? { ...ex, comments: nextComments } : ex
      ),
    });
  };

  /** Latest list from the form to avoid stale closures after async API calls. */
  const getCommentsFromForm = () => {
    const raw =
      form.getState().values?.exercises?.[exerciseIndex]?.comments;
    return Array.isArray(raw) ? raw : [];
  };

  const handleAdd = async () => {
    if (!draft.trim() || !workoutId || submitting) return;
    setSubmitting(true);
    try {
      const newComment = {
        userId: currentUserId,
        userName: currentName || "Coach",
        userType: "coach",
        message: draft.trim(),
        timestamp: Math.floor(Date.now() / 1000),
      };
      const result = await addComment(workoutId, exerciseIndex, newComment);
      if (result !== undefined && result !== false) {
        applyCommentsPristine([...getCommentsFromForm(), newComment]);
        setDraft("");
        setExpanded(true);
      }
    } catch (e) {
      console.error(e);
      alert("Failed to add comment.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editing || !draft.trim() || !workoutId || submitting) return;
    setSubmitting(true);
    try {
      const updated = { ...editing, message: draft.trim() };
      const result = await updateComment(workoutId, exerciseIndex, updated);
      if (result !== undefined && result !== false) {
        applyCommentsPristine(
          getCommentsFromForm().map((c) =>
            c.timestamp === editing.timestamp ? updated : c
          )
        );
        setEditing(null);
        setDraft("");
      }
    } catch (e) {
      console.error(e);
      alert("Failed to update comment.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (c) => {
    if (!workoutId || submitting) return;
    if (!window.confirm("Delete this comment?")) return;
    setSubmitting(true);
    try {
      const result = await deleteComment(
        workoutId,
        exerciseIndex,
        c.timestamp
      );
      if (result !== undefined && result !== false) {
        applyCommentsPristine(
          getCommentsFromForm().filter((x) => x.timestamp !== c.timestamp)
        );
      }
    } catch (e) {
      console.error(e);
      alert("Failed to delete comment.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!workoutId) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Button
        size="small"
        startIcon={<ChatBubbleOutlineIcon />}
        onClick={() => setExpanded((e) => !e)}
        sx={{ mb: 1 }}
      >
        Comments {comments.length > 0 ? `(${comments.length})` : ""}
      </Button>
      <Collapse in={expanded}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Exercise comments
        </Typography>
        {comments.length > 0 ? (
          <Box sx={{ mb: 2 }}>
            {comments.map((c) => (
              <Card key={c.timestamp} variant="outlined" sx={{ mb: 1, p: 1.5 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <Box>
                    <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>
                        {c.userName || "User"}
                      </Typography>
                      <Chip
                        label={c.userType || "user"}
                        size="small"
                        color={
                          c.userType === "coach" ? "primary" : "success"
                        }
                      />
                    </Box>
                    <Typography variant="body2">{c.message}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {moment.unix(c.timestamp).format("MMM D, YYYY h:mm A")}
                    </Typography>
                  </Box>
                  {c.userId === currentUserId && (
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditing(c);
                          setDraft(c.message);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(c)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Card>
            ))}
          </Box>
        ) : (
          <Typography color="text.secondary" variant="body2" sx={{ mb: 1 }}>
            No comments yet
          </Typography>
        )}

        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          placeholder={editing ? "Edit comment…" : "Add a comment…"}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          sx={{ mb: 1 }}
        />
        <LoadingButton
          variant="contained"
          size="small"
          loading={submitting}
          onClick={editing ? handleSaveEdit : handleAdd}
          disabled={!draft.trim()}
        >
          {editing ? "Save" : "Post comment"}
        </LoadingButton>
        {editing && (
          <Button
            size="small"
            onClick={() => {
              setEditing(null);
              setDraft("");
            }}
            sx={{ ml: 1 }}
          >
            Cancel
          </Button>
        )}
      </Collapse>
    </Box>
  );
};

export default ExerciseCommentsSection;
